import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

// import CircularProgress from '@material-ui/core/CircularProgress';

import ApproveUsageIcon from '../../../assets/icons/ApproveUsageIcon';
import ApprovedUsageIcon from '../../../assets/icons/ApprovedUsageIcon';
import ClosedUsageIcon from '../../../assets/icons/CheckIcon';
import ExportIcon from '../../../assets/icons/DownloadIcon';
// import RepeatIcon from '../../../assets/icons/RepeatIcon';

import ActionButton from '../../../components/shared/ActionButton';
import Input from '../../../components/shared/Input';
import LoadScreen from '../../../components/load-screen';
import { UsageTable, UsageFiltersModal, UsageFiltersList } from '../../../components/usage';
// import Button from '../../../components/shared/Button';

import { setFilters, setUsageFilters, toggleClosed, exportOutstandingBilling } from '../../../actions/casesActions';
import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import {
  setOrder,
  setOrderBy,
  setPage,
  setRowsPerPage,
  setSearch,
  setActiveStatuses,
  getCases,
} from '../../../actions/usageActions';

import { useDebounce, useLoading } from '../../../hooks';
// import { getDiffDays } from '../../../utils/date';

import {
  caseStatusOptions,
  caseUsageTypes,
  kitVariantTypes,
  setAllocationStatuses,
  usageItemStatuses,
} from '../../../constants/enums';
import routes from '../../../constants/routes';

import './usage-page.scss';

const { RETURNED, TRANSFERRED, USAGE } = setAllocationStatuses;

const UsagePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const surgeons = useSelector((state) => state.surgeons.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const hideClosed = useSelector((state) => state.cases.hideClosed);
  const filters = useSelector((state) => state.cases.usageFilters);

  const order = useSelector((state) => state.usage.order);
  const orderBy = useSelector((state) => state.usage.orderBy);
  const page = useSelector((state) => state.usage.page);
  const rowsPerPage = useSelector((state) => state.usage.rowsPerPage);
  const search = useSelector((state) => state.usage.search);
  const filter = useSelector((state) => state.usage.activeStatuses);

  const [isOpen, toggleModal] = useState(false);
  // const [refreshLoading, setRefreshLoading] = useState(false);

  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const { loading, stopLoading } = useLoading();

  const debouncedSearch = useDebounce(search, 600);
  const debouncedFilters = useDebounce(filters, 200);

  useEffect(() => {
    onLoad().catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    dispatch(setPage(0));
  }, [hideClosed, filters]);

  const onLoad = async () => {
    try {
      await dispatch(getSurgeons());
      await dispatch(getHospitals());
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    fetchCases().catch();
  }, [page, rowsPerPage, order, orderBy, debouncedSearch, debouncedFilters, filter]);

  const fetchCases = async () => {
    try {
      setCasesLoading(true);

      const statuses = [caseStatusOptions.completed];
      const kitVariants = [kitVariantTypes.loan, kitVariantTypes.consignment];

      const params = {
        search: debouncedSearch || '*',
        sortBy: orderBy || 'date',
        sortOrder: order || 'asc',
        page: page + 1, // page starts from 1
        perPage: rowsPerPage,
        active: true,
        statuses,
        kitVariants,
        hospitals: debouncedFilters.hospitals,
        states: debouncedFilters.states,
        surgeons: debouncedFilters.surgeons,
        procedures: debouncedFilters.procedures,
        orderClosed: false,
      };

      if (filter.approve && !filter.approved) {
        params.usageApproved = false;
        params.hasUsage = true;
      } else if (filter.approved && !filter.approve) {
        params.usageApproved = true;
      } else if (filter.closed) {
        params.orderClosed = true;
      }

      // if (date) {
      //   params.dateFrom = moment(date).startOf('day').valueOf();
      //   params.dateTo = moment(date).endOf('day').valueOf();
      // }

      const res = await dispatch(getCases(params));
      if (res && res.cases) {
        setCases(res.cases);
        setTotalRows(res.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  };

  // const onRefresh = async () => {
  //   try {
  //     setRefreshLoading(true);
  //     await dispatch(getCompletedCases());
  //   } catch (err) {
  //     console.error(err);
  //   } finally {
  //     setRefreshLoading(false);
  //   }
  // };

  const onRowClick = (row) => {
    history.push(`${routes.CASES}/${row?.caseId}?from=usage&view=USAGE`);
  };

  const onClosedClick = async () => {
    dispatch(setActiveStatuses({ approve: false, approved: false, closed: true }));
    dispatch(setPage(0));
  };

  const getSurgeonName = useCallback((surgeonId) => {
    const surgeon = surgeons.find((surgeon) => surgeon.id === surgeonId);
    return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : '';
  }, [surgeons, filters]);

  const getHospitalName = useCallback((hospitalId) => {
    const hospital = hospitals.find((hospital) => hospital.id === hospitalId);
    return hospital ? hospital.name : '';
  }, [hospitals, filters]);

  const getApproved = useCallback((usage) => {
    const implants = usage.filter((item) => item.type === caseUsageTypes.implants);
    const total = implants.length;
    const approved = implants.filter((item) => !!item.approved).length;

    if (!total) {
      return 0;
    }

    return Number(approved);
  }, [cases]);

  const getResolved = useCallback((allocations) => {
    const total = allocations.length;
    const resolved = allocations.filter((item) => [RETURNED.value, USAGE.value, TRANSFERRED.value].includes(item.status)).length;

    if (!total) {
      return 0;
    }

    return resolved;
  }, [cases]);

  const getStatus = useCallback((caseObj, usage = []) => {
    const implants = usage.filter((item) => item.type === caseUsageTypes.implants);
    const total = implants.length;
    const approved = implants.filter((item) => !!item.approved).length;

    if (caseObj.orderClosed) {
      return usageItemStatuses.CLOSED.value;
    }

    if (total === approved) {
      return usageItemStatuses.APPROVED.value;
    }

    return usageItemStatuses.APPROVE.value;
  }, [cases]);

  const getProformaDays = useCallback((caseObj) => {
    if (caseObj.proformaSent) {
      if (!caseObj.proformaSentAt) {
        return 0;
      }

      const date = moment(caseObj.proformaSentAt);
      const now = moment();
      const diffInDays = date.diff(now, 'days');
      return diffInDays;
    }
    return 0;
  }, [cases]);

  const getFinalValue = useCallback((caseItem) => {
    const value = caseItem.orderClosed ? caseItem.finalOrderValue : caseItem.proformaValue;

    if (!value || !Number(value)) {
      return 0;
    }

    return Number(value);
  }, [cases]);

  const onExportClick = async () => {
    try {
      setExportLoading(true);
      await dispatch(exportOutstandingBilling());
    } catch (err) {
      console.error(err);
    } finally {
      setExportLoading(false);
    }
  };

  const list = useMemo(() => {
    // let result = cases.filter((item) => item.status === caseStatusOptions.completed && (item?.patientReference?.toLowerCase()?.includes(search.toLowerCase()) || item?.id?.toLowerCase()?.includes(search.toLowerCase())));

    // result = result.filter((item) => {
    //   const implants = item.usage ? item.usage.filter((item) => item.type === caseUsageTypes.implants) : [];
    //   const total = implants.length;
    //   const approved = implants.filter((usage) => !!usage.approved).length;
    //
    //   if (filter.closed) {
    //     return item.orderClosed;
    //   } else {
    //     if (filter.approve && filter.approved && !item.orderClosed) {
    //       return true;
    //     }
    //
    //     if (!filter.approve && !filter.approved) {
    //       return false;
    //     }
    //   }
    //
    //   return ((!filter.approve && total === approved) || (!filter.approved && total > approved)) && !item.orderClosed;
    // });
    //
    // if (filters.surgeons?.length) {
    //   result = result.filter((item) => filters.surgeons?.includes(item.surgeon));
    // }
    //
    // if (filters.hospitals?.length) {
    //   result = result.filter((item) => filters.hospitals?.includes(item.hospital));
    // }
    //
    // if (hideClosed) {
    //   result = result.filter((item) => !item.orderClosed);
    // }

    return cases
      .map((item) => ({
        ...item,
        surgeonName: getSurgeonName(item.surgeon),
        hospitalName: getHospitalName(item.hospital),
        approved: getApproved(item?.usage || []),
        approveStatus: getStatus(item, item?.usage || []),
        resolved: getResolved(item?.setsAllocation || []),
        proformaStatus: getProformaDays(item),
        finalValue: getFinalValue(item)
      }))
      // .filter((item) => {
      //   if (!item.orderClosedAt) {
      //     return true;
      //   }
      //
      //   const diff = getDiffDays(moment(), moment(item.orderClosedAt))
      //   return diff < 60;
      // });
  }, [cases, getSurgeonName, getHospitalName]);

  const onRemoveFilter = (filter, value) => {
    dispatch(setPage(0));

    if (filter === 'closed') {
      dispatch(toggleClosed());
    }
    if (filter === 'surgeon') {
      dispatch(setFilters({ surgeons: [...filters?.surgeons].filter((surgeon) => surgeon !== value) }));
    }
    if (filter === 'hospital') {
      dispatch(setFilters({ hospitals: [...filters?.hospitals].filter((hospital) => hospital !== value) }));
    }
  };

  return (
    <div className='page-container kits-page'>
      { loading && <LoadScreen /> }

      <div className='d-flex space-between'>
        <div className='d-flex'>
          <div className='page-title'>
            Usage
          </div>
        </div>
      </div>

      <div className='kits-page__body'>
        <>
          <div className='d-flex space-between'>
            <div className='d-flex'>
              <div className='m-r-md'>
                <ActionButton
                  onClick={() => {
                    dispatch(setActiveStatuses({ ...filter, approve: !filter.approve, closed: false }));
                    dispatch(setPage(0));
                  }}
                  bgColor={!!filter.approve ? setAllocationStatuses.TRANSFERRED.color : '#dddddd'}
                >
                  <ApproveUsageIcon color="#ffffff" />
                  Approve
                </ActionButton>
              </div>
              <div className='m-l-md m-r-md'>
                <ActionButton
                  onClick={() => {
                    dispatch(setActiveStatuses({ ...filter, approved: !filter.approved, closed: false }));
                    dispatch(setPage(0));
                  }}
                  bgColor={!!filter.approved ? setAllocationStatuses.SHIPPED.color : '#dddddd'}
                >
                  <ApprovedUsageIcon color="#ffffff" />
                  Approved
                </ActionButton>
              </div>
              <div className='m-l-md'>
                <ActionButton
                  onClick={onClosedClick}
                  bgColor={!!filter.closed ? setAllocationStatuses.RETURNED.color : '#dddddd'}
                >
                  <ClosedUsageIcon color="#ffffff" />
                  Closed
                </ActionButton>
              </div>
            </div>

            <div className='d-flex flex-end' style={{ width: '40%' }}>
              <div className='m-b-lg m-r-lg'>
                {/*<Button*/}
                {/*  type='icon'*/}
                {/*  onClick={onRefresh}*/}
                {/*  disabled={refreshLoading}*/}
                {/*  loading={refreshLoading}*/}
                {/*>*/}
                {/*  <RepeatIcon />*/}
                {/*</Button>*/}
              </div>

              {(filter.approve || filter.approved) && (
                <div style={{ marginBottom: '12px', marginRight: '16px' }}>
                  <ActionButton
                    onClick={onExportClick}
                    bgColor={setAllocationStatuses.SHIPPED.color}
                    disabled={exportLoading}
                    loading={exportLoading}
                  >
                    <ExportIcon color="#ffffff" />
                    Export
                  </ActionButton>
                </div>
              )}

              <div className='flex-1' style={{ maxWidth: 500 }}>
                <Input
                  type='search'
                  placeholder='Search Cases (Patient ref or Case #)'
                  value={search}
                  onChange={(e) => {
                    dispatch(setPage(0));
                    dispatch(setSearch(e.target.value));
                  }}
                />
              </div>
            </div>
          </div>

          <UsageFiltersList
            onClick={() => toggleModal(true)}
            hideClosed={hideClosed}
            surgeons={filters?.surgeons}
            hospitals={filters?.hospitals}
            surgeonsList={surgeons}
            hospitalsList={hospitals}
            onDelete={onRemoveFilter}
          />

          <UsageTable
            cases={list}
            onRowClick={onRowClick}
            surgeons={surgeons}
            hospitals={hospitals}
            order={order}
            setOrder={(value) => dispatch(setOrder(value))}
            orderBy={orderBy}
            setOrderBy={(value) => dispatch(setOrderBy(value))}
            page={page}
            setPage={(value) => dispatch(setPage(value))}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
            loading={casesLoading}
            totalRows={totalRows}
          />

          {/*{!!filter.closed && !completedLoaded ? (*/}
          {/*  <div className='d-flex flex-center p-b-lg'>*/}
          {/*    <CircularProgress />*/}
          {/*  </div>*/}
          {/*) : (*/}
          {/*  <UsageTable*/}
          {/*    cases={list}*/}
          {/*    onRowClick={(item) => onRowClick(item.id)}*/}
          {/*    surgeons={surgeons}*/}
          {/*    hospitals={hospitals}*/}
          {/*    order={order}*/}
          {/*    setOrder={(value) => dispatch(setOrder(value))}*/}
          {/*    orderBy={orderBy}*/}
          {/*    setOrderBy={(value) => dispatch(setOrderBy(value))}*/}
          {/*    page={page}*/}
          {/*    setPage={(value) => dispatch(setPage(value))}*/}
          {/*    rowsPerPage={rowsPerPage}*/}
          {/*    setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}*/}
          {/*  />*/}
          {/*)}*/}
        </>
      </div>

      <UsageFiltersModal
        open={isOpen}
        onClose={() => toggleModal(false)}
        hideClosed={hideClosed}
        toggleClosed={() => dispatch(toggleClosed())}
        selectedSurgeons={filters.surgeons}
        selectedHospitals={filters.hospitals}
        onSurgeonChange={(value) => dispatch(setUsageFilters({ surgeons: value }))}
        onHospitalChange={(value) => dispatch(setUsageFilters({ hospitals: value }))}
        surgeons={surgeons}
        hospitals={hospitals}
      />
    </div>
  );
};

export default UsagePage;
